import {
	HTMLAttributes,
	PropsWithChildren,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useEffectOnce } from 'react-use';
import { useZoomFactor } from '~/hooks';
import {
	StyledContainer,
	StyledScrollbarThumb,
	StyledScrollbarTrack,
	StyledScrollContainer,
} from './ScrollContainer.styled';

type ScrollableElement = HTMLDivElement | HTMLTextAreaElement;

const SCROLLBAR_TRACK_PADDING = 2;

const ScrollContainer = ({
	children,
	width,
	height,
	scrollbarHeight,
	scrollbarRight = 10,
	scrollbarTop = 0,
	showMask = false,
	scrollableRef,
	scrollContainerRef,
	scrollbarThumbColor,
	scrollbarThumbHoverColor,
	scrollbarBlackLine = false,
	...props
}: PropsWithChildren &
	HTMLAttributes<HTMLDivElement> & {
		width?: number;
		height: number;
		scrollbarHeight?: number;
		scrollbarRight?: number;
		scrollbarTop?: number;
		showMask?: boolean;
		/** Ref на внешний скроллируемый элемент. Когда передан, скроллбар синхронизируется с ним вместо внутреннего div. */
		scrollableRef?: React.RefObject<ScrollableElement | null>;
		/** Ref на внутренний скроллируемый div контейнера. */
		scrollContainerRef?: React.RefObject<HTMLDivElement>;
		/** Цвет ползунка скроллбара (по умолчанию белый) */
		scrollbarThumbColor?: string;
		/** Цвет ползунка при наведении (по умолчанию белый 80%) */
		scrollbarThumbHoverColor?: string;
		scrollbarBlackLine?: boolean;
	}) => {
	const internalListRef = useRef<HTMLDivElement>(null);
	const internalScrollRef = scrollContainerRef ?? internalListRef;
	const listRef = scrollableRef ?? internalScrollRef;
	const useExternalScrollable = Boolean(scrollableRef);
	const [showScrollbar, setShowScrollbar] = useState(false);
	const [scrollThumbHeight, setScrollThumbHeight] = useState(0);
	const [scrollThumbTop, setScrollThumbTop] = useState(0);
	const [isDragging, setIsDragging] = useState(false);
	const [dragStartOffset, setDragStartOffset] = useState(0);
	const [showTopMask, setShowTopMask] = useState(false);
	const [showBottomMask, setShowBottomMask] = useState(false);

	const zoomFactor = useZoomFactor();

	const actualScrollbarHeight = scrollbarHeight ?? height;
	const trackUsableHeight = actualScrollbarHeight - SCROLLBAR_TRACK_PADDING * 2;
	const TRACK_PADDING = 2;

	const right = useMemo(() => -10 - scrollbarRight, [scrollbarRight]);

	useEffect(() => {
		const el = listRef.current;

		const checkScrollbar = () => {
			if (listRef.current) {
				const { scrollHeight, clientHeight } = listRef.current;
				const needsScrollbar = scrollHeight > clientHeight;
				setShowScrollbar(needsScrollbar);

				if (needsScrollbar) {
					const thumbHeight = Math.max(
						20,
						Math.min(
							trackUsableHeight,
							(clientHeight / scrollHeight) * trackUsableHeight,
						),
					);
					setScrollThumbHeight(thumbHeight);
				}
			}
		};

		checkScrollbar();
		if (!el && scrollableRef) {
			requestAnimationFrame(checkScrollbar);
		}
		window.addEventListener('resize', checkScrollbar);
		if (useExternalScrollable && el && 'value' in el) {
			el.addEventListener('input', checkScrollbar);
			return () => {
				window.removeEventListener('resize', checkScrollbar);
				el.removeEventListener('input', checkScrollbar);
			};
		}
		return () => window.removeEventListener('resize', checkScrollbar);
	}, [children, trackUsableHeight, useExternalScrollable, scrollableRef]);

	useEffect(() => {
		const updateThumbPosition = () => {
			if (listRef.current && showScrollbar) {
				const { scrollTop, scrollHeight, clientHeight } = listRef.current;
				const maxScrollTop = scrollHeight - clientHeight;
				const maxThumbTop =
					actualScrollbarHeight - scrollThumbHeight - SCROLLBAR_TRACK_PADDING;
				const thumbTop = Math.min(
					SCROLLBAR_TRACK_PADDING +
						(maxScrollTop > 0
							? (scrollTop / maxScrollTop) *
							  (trackUsableHeight - scrollThumbHeight)
							: 0),
					maxThumbTop,
				);
				setScrollThumbTop(thumbTop);
			}
		};

		const updateMaskVisibility = () => {
			if (listRef.current && showMask) {
				const { scrollTop, scrollHeight, clientHeight } = listRef.current;
				const maxScrollTop = scrollHeight - clientHeight;

				// Показываем верхнюю маску, если не в самом верху
				setShowTopMask(scrollTop > 0);

				// Показываем нижнюю маску, если не в самом низу
				setShowBottomMask(scrollTop < maxScrollTop);
			}
		};

		const handleScroll = () => {
			updateThumbPosition();
			updateMaskVisibility();
		};

		const listElement = listRef.current;
		if (listElement) {
			listElement.addEventListener('scroll', handleScroll);
			// Инициализируем маски при монтировании
			updateThumbPosition();
			updateMaskVisibility();
			return () => listElement.removeEventListener('scroll', handleScroll);
		}
	}, [
		showScrollbar,
		scrollThumbHeight,
		actualScrollbarHeight,
		showMask,
		TRACK_PADDING,
	]);

	const handleThumbMouseDown = (e: React.MouseEvent) => {
		e.preventDefault();
		if (!listRef.current) return;

		const thumbElement = e.currentTarget as HTMLElement;
		const thumbRect = thumbElement.getBoundingClientRect();

		const clickOffsetInThumb = e.clientY - thumbRect.top;

		setDragStartOffset(clickOffsetInThumb);
		setIsDragging(true);
	};

	useEffect(() => {
		const handleMouseMove = (e: MouseEvent) => {
			if (!isDragging || !listRef.current || !showScrollbar) return;

			const listRect = listRef.current.getBoundingClientRect();
			const trackHeightInScreenPixels = actualScrollbarHeight * zoomFactor;
			const thumbHeightInScreenPixels = scrollThumbHeight * zoomFactor;
			const paddingInScreenPixels = TRACK_PADDING * zoomFactor;
			const thumbY = e.clientY - listRect.top - dragStartOffset;
			const availableHeight =
				trackHeightInScreenPixels -
				thumbHeightInScreenPixels -
				paddingInScreenPixels * 2;
			const thumbYRelative = thumbY - paddingInScreenPixels;
			const scrollRatio = Math.max(
				0,
				Math.min(1, thumbYRelative / availableHeight),
			);

			const { scrollHeight, clientHeight } = listRef.current;
			const maxScrollTop = scrollHeight - clientHeight;
			const newScrollTop = scrollRatio * maxScrollTop;

			listRef.current.scrollTop = Math.max(
				0,
				Math.min(maxScrollTop, newScrollTop),
			);
		};

		const handleMouseUp = () => {
			setIsDragging(false);
		};

		if (isDragging) {
			document.addEventListener('mousemove', handleMouseMove);
			document.addEventListener('mouseup', handleMouseUp);
			return () => {
				document.removeEventListener('mousemove', handleMouseMove);
				document.removeEventListener('mouseup', handleMouseUp);
			};
		}
	}, [
		isDragging,
		showScrollbar,
		trackUsableHeight,
		zoomFactor,
		dragStartOffset,
		scrollThumbHeight,
		TRACK_PADDING,
	]);

	useEffectOnce(() => {
		if (scrollableRef) return;
		const preventArrowScroll = (e: KeyboardEvent) => {
			if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
				e.preventDefault();
			}
		};

		document.addEventListener('keydown', preventArrowScroll);
		return () => document.removeEventListener('keydown', preventArrowScroll);
	});

	if (scrollableRef) {
		return (
			<StyledContainer {...props}>
				{children}
				{showScrollbar && (
					<StyledScrollbarTrack
						style={{ height: actualScrollbarHeight, right }}
					>
						<StyledScrollbarThumb
							$thumbColor={scrollbarThumbColor}
							$thumbHoverColor={scrollbarThumbHoverColor}
							style={{
								height: scrollThumbHeight,
								transform: `translateY(${scrollThumbTop}px)`,
							}}
							onMouseDown={handleThumbMouseDown}
						/>
					</StyledScrollbarTrack>
				)}
			</StyledContainer>
		);
	}

	return (
		<StyledContainer {...props}>
			<StyledScrollContainer
				style={{
					width: width ?? '100%',
					height,
					['--topFade' as any]: showMask && showTopMask ? '50px' : '0px',
					['--bottomFade' as any]: showMask && showBottomMask ? '50px' : '0px',
				}}
				ref={internalScrollRef}
			>
				{children}
			</StyledScrollContainer>
			{showScrollbar && (
				<StyledScrollbarTrack
					style={{ height: actualScrollbarHeight, right, top: scrollbarTop }}
					$scrollbarBlackLine={scrollbarBlackLine}
				>
					<StyledScrollbarThumb
						$thumbColor={scrollbarThumbColor}
						$thumbHoverColor={scrollbarThumbHoverColor}
						style={{
							height: scrollThumbHeight,

							transform: `translateY(${scrollThumbTop}px)`,
						}}
						$scrollbarBlackLine={scrollbarBlackLine}
						onMouseDown={handleThumbMouseDown}
					/>
				</StyledScrollbarTrack>
			)}
		</StyledContainer>
	);
};

export { ScrollContainer };
